<template>
    <b-modal
        :title="$t('history.modal.header.SendEmails')"
        id="sendEmails-modal"
        ref="sendEmails-modal"
        @ok="handleOk"
        @cancel="reset"
        @hide="reset"
    >
        <form ref="form" class="required-icon" @submit.stop.prevent="handleSubmit">
            <b-form-group :label="$t('history.modal.body.Templates')">
                <b-row>
                    <b-col sm="11">
                        <vSelect
                            :dynamicWidth="false"
                            :options="templates"
                            label="templateName"
                            :placeholder="$t('createShipment.placeholder.Search Templates')"
                            v-model="selectedTemplate"
                            :class="{ 'is-invalid': $v.selectedTemplate.$error }"
                            @update="$v.selectedTemplate.$touch()"
                            :clearable="false"
                        />
                        <b-form-invalid-feedback v-if="$v.selectedTemplate.$error">
                            <validationRequired
                                fieldName="Template"
                                :fieldValid="$v.selectedTemplate.required"
                            />
                        </b-form-invalid-feedback>
                    </b-col>

                    <b-input-group-append>
                        <requiredIcon />
                    </b-input-group-append>
                </b-row>
            </b-form-group>

            <b-form-group :label="$t('history.modal.body.Subject')">
                <b-row>
                    <b-col sm="11">
                        <b-form-input
                            size="sm"
                            v-model="selectedTemplate.subject"
                            name="emailSubjectInput"
                            :class="{ 'is-invalid': $v.selectedTemplate.subject.$error }"
                            @update="$v.selectedTemplate.subject.$touch()"
                        ></b-form-input>

                        <b-form-invalid-feedback v-if="$v.selectedTemplate.subject.$error">
                            <validationRequired
                                fieldName="Subject"
                                :fieldValid="$v.selectedTemplate.subject.required"
                            />
                        </b-form-invalid-feedback>
                    </b-col>
                    <b-input-group-append>
                        <requiredIcon />
                    </b-input-group-append>
                </b-row>
            </b-form-group>
            <b-form-group :label="$t('history.modal.body.SendFromEmail')">
                <b-row>
                    <b-col sm="11">
                        <b-form-input
                            size="sm"
                            v-model="selectedTemplate.senderEmail"
                            required
                            name="emailFromInput"
                            :class="{ 'is-invalid': $v.selectedTemplate.senderEmail.$error }"
                            @update="$v.selectedTemplate.senderEmail.$touch()"
                        ></b-form-input>

                        <b-form-invalid-feedback v-if="$v.selectedTemplate.senderEmail.$error">
                            <validationRequired
                                fieldName="Sender Email"
                                :fieldValid="$v.selectedTemplate.senderEmail.required"
                            />
                        </b-form-invalid-feedback>
                    </b-col>
                    <b-input-group-append>
                        <requiredIcon />
                    </b-input-group-append>
                </b-row>
            </b-form-group>

            <b-form-group :label="$t('history.modal.body.SendToEmail')">
                <b-row>
                    <b-col sm="11">
                        <b-form-input
                            size="sm"
                            v-model="selectedTemplate.receiverEmail"
                            required
                            name="emailToInput"
                            :class="{ 'is-invalid': $v.selectedTemplate.receiverEmail.$error }"
                            @update="$v.selectedTemplate.receiverEmail.$touch()"
                        ></b-form-input>

                        <b-form-invalid-feedback v-if="$v.selectedTemplate.receiverEmail.$error">
                            <validationRequired
                                fieldName="Receiver Email"
                                :fieldValid="$v.selectedTemplate.receiverEmail.required"
                            />
                        </b-form-invalid-feedback>
                    </b-col>
                    <b-input-group-append>
                        <requiredIcon />
                    </b-input-group-append>
                </b-row>
            </b-form-group>

            <b-form-group :label="$t('history.modal.body.CC')">
                <b-row>
                    <b-col sm="11">
                        <b-form-input
                            size="sm"
                            v-model="selectedTemplate.carbonCopy"
                            required
                            name="emailCCInput"
                        ></b-form-input>
                    </b-col>
                </b-row>
            </b-form-group>

            <b-form-group :label="$t('history.modal.body.BCC')">
                <b-row>
                    <b-col sm="11">
                        <b-form-input
                            size="sm"
                            v-model="selectedTemplate.blindCarbonCopy"
                            required
                            name="emailBCCInput"
                        ></b-form-input>
                    </b-col>
                </b-row>
            </b-form-group>
        </form>

        <template v-slot:modal-footer="{ ok, cancel }">
            <b-button @click="cancel()">
                Cancel
            </b-button>
            <b-button @click="ok()" name="sendEmailModalButton" variant="primary">
                {{ $t('history.modal.button.Send') }}
            </b-button>
        </template>
    </b-modal>
</template>
<script>
import { CORE_PREFIX, ACTIONS, MUTATIONS, HISTORY_PREFIX } from '@/store/constants';
import helpers from '@/helper';
import vSelect from '../../vSelect';
import requiredIcon from '../../requiredIcon';
import validationRequired from '../../Validation/ValidationRequired';
import historyActionsSendEmailsValidationMixin from '@/mixins/historyActionsSendEmailsValidationMixin';

export default {
    components: {
        vSelect,
        requiredIcon,
        validationRequired
    },

    mixins: [historyActionsSendEmailsValidationMixin],

    props: {
        shipment: { type: Object, required: true }
    },

    data() {
        return {
            templates: [],
            selectedTemplate: {
                templateName: '',
                subject: '',
                senderEmail: '',
                receiverMail: ''
            },

            currentEmail: this.$AuthService.getUserMail()
        };
    },

    methods: {
        sendEmail() {
            this.$v.selectedTemplate.$touch();
            if (this.$v.selectedTemplate.$invalid) {
                return;
            }

            this.$store.dispatch(HISTORY_PREFIX + ACTIONS.HISTORY.SEND_EMAIL_TEMPLATE, {
                template: this.selectedTemplate,
                shipmentId: this.shipment.id
            });

            this.$nextTick(() => {
                this.$bvModal.hide('sendEmails-modal');
            });
        },

        async showSendEmailsModal() {
            const response = await this.$store.dispatch(
                HISTORY_PREFIX + ACTIONS.HISTORY.FETCH_EMAIL_TEMPLATES,
                this.shipment.id
            );

            this.templates = response.data;

            this.templates.forEach((key) => {
                key.senderEmail = this.currentEmail;
            });

            this.$refs['sendEmails-modal'].show();
        },

        handleOk(bvModalEvt) {
            bvModalEvt.preventDefault();

            this.sendEmail();
        },

        reset() {
            this.templates = [];
            this.selectedTemplate = {};
            this.$v.$reset();
        }
    }
};
</script>

<style>
.input-group-append {
    align-items: center;
}
</style>
